import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { ReadableNote } from '../components/ReadableNote';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Heart,
  Users,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Minus,
  Moon,
  Battery,
  Activity,
  Brain,
  Smile,
} from 'lucide-react';
import { toast } from 'sonner';

const METRIC_ICONS = {
  sleep_score: Moon,
  fatigue_score: Battery,
  soreness_score: Activity,
  stress_score: Brain,
  mood_score: Smile,
};

const getReadinessColor = (score) => {
  if (score >= 60) return 'text-emerald-400';
  if (score >= 40) return 'text-amber-400';
  return 'text-rose-400';
};

const getReadinessBg = (score) => {
  if (score >= 60) return 'bg-emerald-500/10 border-emerald-500/30';
  if (score >= 40) return 'bg-amber-500/10 border-amber-500/30';
  return 'bg-rose-500/10 border-rose-500/30';
};

const getAlertBadge = (level) => {
  if (level === 'GREEN') return { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Good' };
  if (level === 'AMBER') return { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Alert' };
  return { bg: 'bg-rose-500/20', text: 'text-rose-400', label: 'Warning' };
};

const StaffWellnessPage = () => {
  const { api, hasRole } = useAuth();
  const { t, language } = useLanguage();

  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [wellnessData, setWellnessData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('overview'); // overview | alerts | missing

  const isReadOnly = hasRole(['COACH']);

  const fetchTeams = useCallback(async () => {
    try {
      const response = await api.get('/teams');
      setTeams(response.data);
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    }
  }, [api]);

  const fetchWellnessData = useCallback(async () => {
    setIsLoading(true);
    try {
      let endpoint = '/wellness/today';
      const params = new URLSearchParams();
      
      // For non-today dates, use team endpoint
      if (selectedDate !== new Date().toISOString().split('T')[0]) {
        if (selectedTeam !== 'all') {
          // Single team - use team endpoint
          const response = await api.get(`/wellness/team/${selectedTeam}?date=${selectedDate}`);
          setWellnessData({
            date: selectedDate,
            entries: response.data.entries,
            missing: response.data.missing,
            stats: response.data.stats,
            alerts: response.data.alerts
          });
          setIsLoading(false);
          return;
        } else {
          // All teams - fetch from each accessible team and combine
          const summaryResponse = await api.get(`/wellness/summary?date=${selectedDate}`);
          
          // Get all team entries
          let allEntries = [];
          let allMissing = [];
          let allAlerts = { red: [], amber: [] };
          
          // Fetch entries for each team the user has access to
          if (teams.length > 0) {
            const teamPromises = teams.map(team => 
              api.get(`/wellness/team/${team.id}?date=${selectedDate}`).catch(() => null)
            );
            const teamResponses = await Promise.all(teamPromises);
            
            teamResponses.forEach(response => {
              if (response && response.data) {
                if (response.data.entries) allEntries.push(...response.data.entries);
                if (response.data.missing) allMissing.push(...response.data.missing);
                if (response.data.alerts?.red) allAlerts.red.push(...response.data.alerts.red);
                if (response.data.alerts?.amber) allAlerts.amber.push(...response.data.alerts.amber);
              }
            });
          }
          
          setWellnessData({
            date: selectedDate,
            entries: allEntries,
            missing: allMissing,
            stats: summaryResponse.data.stats,
            alerts: allAlerts
          });
          setIsLoading(false);
          return;
        }
      }
      
      // For today
      if (selectedTeam !== 'all') {
        params.append('team_id', selectedTeam);
      }
      
      const response = await api.get(`${endpoint}?${params.toString()}`);
      setWellnessData(response.data);
    } catch (err) {
      console.error('Failed to fetch wellness data:', err);
      toast.error('Failed to load wellness data');
    } finally {
      setIsLoading(false);
    }
  }, [api, selectedTeam, selectedDate, teams]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  useEffect(() => {
    fetchWellnessData();
  }, [fetchWellnessData]);

  const handlePrevDay = () => {
    const prev = new Date(selectedDate);
    prev.setDate(prev.getDate() - 1);
    setSelectedDate(prev.toISOString().split('T')[0]);
  };

  const handleNextDay = () => {
    const next = new Date(selectedDate);
    next.setDate(next.getDate() + 1);
    setSelectedDate(next.toISOString().split('T')[0]);
  };

  const handleToday = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'IT' ? 'it-IT' : 'en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const isToday = selectedDate === new Date().toISOString().split('T')[0];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-zinc-500">{t('loading')}</div>
      </div>
    );
  }

  const stats = wellnessData?.stats || {};
  const entries = wellnessData?.entries || [];
  const missing = wellnessData?.missing || [];
  const alerts = wellnessData?.alerts || { red: [], amber: [] };

  // Sort entries by readiness (lowest first for alerts)
  const sortedEntries = [...entries].sort((a, b) => a.readiness_score - b.readiness_score);
  const redAlerts = isReadOnly ? [] : (alerts.red?.length > 0 ? alerts.red : entries.filter(e => e.alert_level === 'RED'));
  const amberAlerts = isReadOnly ? [] : (alerts.amber?.length > 0 ? alerts.amber : entries.filter(e => e.alert_level === 'AMBER'));

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
            <Heart className="w-6 h-6 text-rose-400" />
            {language === 'IT' ? 'Wellness' : 'Wellness'}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {language === 'IT' ? 'Monitoraggio readiness atleti' : 'Athlete readiness monitoring'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleToday}
            variant="outline"
            size="sm"
            className={`border-zinc-700 text-zinc-300 ${isToday ? 'bg-zinc-800' : ''}`}
            data-testid="today-btn"
          >
            {language === 'IT' ? 'Oggi' : 'Today'}
          </Button>
          <div className="flex items-center bg-zinc-900 border border-zinc-800 rounded-sm">
            <Button
              onClick={handlePrevDay}
              variant="ghost"
              size="icon"
              className="text-zinc-400 hover:text-zinc-100 h-8 w-8"
              data-testid="prev-day"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-[140px] bg-transparent border-0 text-center text-zinc-100"
              data-testid="date-input"
            />
            <Button
              onClick={handleNextDay}
              variant="ghost"
              size="icon"
              className="text-zinc-400 hover:text-zinc-100 h-8 w-8"
              data-testid="next-day"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <Select value={selectedTeam} onValueChange={setSelectedTeam}>
          <SelectTrigger className="w-[200px] bg-zinc-900 border-zinc-800" data-testid="team-filter">
            <SelectValue placeholder="All Teams" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">{t('all')} {t('teams')}</SelectItem>
            {teams.map((team) => (
              <SelectItem key={team.id} value={team.id}>{team.code} - {team.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-sm p-1">
          <button
            onClick={() => setViewMode('overview')}
            className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${
              viewMode === 'overview' ? 'bg-zinc-800 text-zinc-100' : 'text-zinc-400 hover:text-zinc-100'
            }`}
            data-testid="view-overview"
          >
            {language === 'IT' ? 'Panoramica' : 'Overview'}
          </button>
          {!isReadOnly && (
            <>
              <button
                onClick={() => setViewMode('alerts')}
                className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${
                  viewMode === 'alerts' ? 'bg-zinc-800 text-zinc-100' : 'text-zinc-400 hover:text-zinc-100'
                }`}
                data-testid="view-alerts"
              >
                {language === 'IT' ? 'Allerta' : 'Alerts'} ({redAlerts.length + amberAlerts.length})
              </button>
              <button
                onClick={() => setViewMode('missing')}
                className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${
                  viewMode === 'missing' ? 'bg-zinc-800 text-zinc-100' : 'text-zinc-400 hover:text-zinc-100'
                }`}
                data-testid="view-missing"
              >
                {language === 'IT' ? 'Mancanti' : 'Missing'} ({stats.missing_count || 0})
              </button>
            </>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <Users className="w-5 h-5 text-zinc-500 mx-auto mb-2" />
            <div className="text-2xl font-bold text-zinc-100">{stats.total_athletes || 0}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">
              {language === 'IT' ? 'Atleti' : 'Athletes'}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto mb-2" />
            <div className="text-2xl font-bold text-emerald-400">{stats.submitted || 0}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">
              {language === 'IT' ? 'Inviati' : 'Submitted'}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <XCircle className="w-5 h-5 text-zinc-500 mx-auto mb-2" />
            <div className="text-2xl font-bold text-zinc-400">{stats.missing_count || 0}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">
              {language === 'IT' ? 'Mancanti' : 'Missing'}
            </div>
          </CardContent>
        </Card>
        <Card className={`border ${stats.avg_readiness >= 60 ? 'border-emerald-500/30 bg-emerald-500/5' : stats.avg_readiness >= 40 ? 'border-amber-500/30 bg-amber-500/5' : 'border-rose-500/30 bg-rose-500/5'}`}>
          <CardContent className="p-4 text-center">
            <Heart className={`w-5 h-5 mx-auto mb-2 ${getReadinessColor(stats.avg_readiness || 0)}`} />
            <div className={`text-2xl font-bold ${getReadinessColor(stats.avg_readiness || 0)}`}>
              {stats.avg_readiness || 0}
            </div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">
              {language === 'IT' ? 'Readiness Medio' : 'Avg Readiness'}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="w-5 h-5 text-rose-400 mx-auto mb-2" />
            <div className="flex items-center justify-center gap-2">
              <span className="text-lg font-bold text-rose-400">{stats.red_count || stats.red_alerts || 0}</span>
              <span className="text-zinc-600">/</span>
              <span className="text-lg font-bold text-amber-400">{stats.amber_count || stats.amber_alerts || 0}</span>
            </div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">
              {language === 'IT' ? 'Allerta' : 'Alerts'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      {viewMode === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* All Submissions */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-zinc-100 text-lg">
                {language === 'IT' ? 'Risposte di Oggi' : "Today's Submissions"}
              </CardTitle>
              <CardDescription className="text-zinc-500">
                {entries.length} {language === 'IT' ? 'risposte ricevute' : 'responses received'}
              </CardDescription>
            </CardHeader>
            <CardContent className="max-h-[400px] overflow-y-auto">
              {entries.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  {language === 'IT' ? 'Nessuna risposta' : 'No submissions yet'}
                </div>
              ) : (
                <div className="space-y-2">
                  {sortedEntries.map((entry) => {
                    const badge = getAlertBadge(entry.alert_level);
                    return (
                      <div
                        key={entry.id}
                        className={`p-3 rounded-sm border ${getReadinessBg(entry.readiness_score)}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-sm flex items-center justify-center text-lg font-bold ${getReadinessColor(entry.readiness_score)}`}>
                              {entry.readiness_score}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-zinc-100">{entry.athlete_name}</div>
                              <div className="text-xs text-zinc-500">
                                {entry.team_code} • {entry.athlete_position}
                              </div>
                            </div>
                          </div>
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${badge.bg} ${badge.text}`}>
                            {badge.label}
                          </span>
                        </div>
                        {/* Individual Scores */}
                        <div className="flex flex-wrap items-center gap-4 mt-2 pt-2 border-t border-zinc-700/50 min-w-0">
                          <div className="flex items-center gap-1" title={language === 'IT' ? 'Sonno' : 'Sleep'}>
                            <Moon className="w-3.5 h-3.5 text-blue-400" />
                            <span className="text-xs text-zinc-300">{entry.sleep_score}</span>
                          </div>
                          <div className="flex items-center gap-1" title={language === 'IT' ? 'Fatica' : 'Fatigue'}>
                            <Battery className="w-3.5 h-3.5 text-amber-400" />
                            <span className="text-xs text-zinc-300">{entry.fatigue_score}</span>
                          </div>
                          <div className="flex items-center gap-1" title={language === 'IT' ? 'Dolore' : 'Soreness'}>
                            <Activity className="w-3.5 h-3.5 text-rose-400" />
                            <span className="text-xs text-zinc-300">{entry.soreness_score}</span>
                          </div>
                          <div className="flex items-center gap-1" title={language === 'IT' ? 'Stress' : 'Stress'}>
                            <Brain className="w-3.5 h-3.5 text-purple-400" />
                            <span className="text-xs text-zinc-300">{entry.stress_score}</span>
                          </div>
                          <div className="flex items-center gap-1" title={language === 'IT' ? 'Umore' : 'Mood'}>
                            <Smile className="w-3.5 h-3.5 text-emerald-400" />
                            <span className="text-xs text-zinc-300">{entry.mood_score}</span>
                          </div>
                          <ReadableNote testId={`wellness-note-${entry.id}`}>{entry.notes}</ReadableNote>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Alerts Summary (for non-COACH) */}
          {!isReadOnly && (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-zinc-100 text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-rose-400" />
                  {language === 'IT' ? 'Allerte' : 'Alerts'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Red Alerts */}
                {redAlerts.length > 0 && (
                  <div>
                    <div className="text-xs text-rose-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {language === 'IT' ? 'Allerta Rossa' : 'Red Alert'} ({redAlerts.length})
                    </div>
                    <div className="space-y-2">
                      {redAlerts.slice(0, 5).map((alert, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-2 bg-rose-500/10 border border-rose-500/30 rounded-sm"
                        >
                          <div className="text-sm text-zinc-100">
                            {alert.athlete_name || alert.name}
                          </div>
                          <div className="text-sm font-bold text-rose-400">
                            {alert.readiness_score || alert.readiness}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Amber Alerts */}
                {amberAlerts.length > 0 && (
                  <div>
                    <div className="text-xs text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {language === 'IT' ? 'Allerta Gialla' : 'Yellow Alert'} ({amberAlerts.length})
                    </div>
                    <div className="space-y-2">
                      {amberAlerts.slice(0, 5).map((alert, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-2 bg-amber-500/10 border border-amber-500/30 rounded-sm"
                        >
                          <div className="text-sm text-zinc-100">
                            {alert.athlete_name || alert.name}
                          </div>
                          <div className="text-sm font-bold text-amber-400">
                            {alert.readiness_score || alert.readiness}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {redAlerts.length === 0 && amberAlerts.length === 0 && (
                  <div className="text-center py-8 text-zinc-500">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                    {language === 'IT' ? 'Nessuna allerta' : 'No alerts'}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Coach sees read-only message */}
          {isReadOnly && (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-6 text-center">
                <AlertCircle className="w-8 h-8 mx-auto mb-3 text-zinc-500" />
                <p className="text-zinc-400">
                  {language === 'IT' 
                    ? 'Accesso in sola lettura. Contatta lo staff per i dettagli degli atleti.'
                    : 'Read-only access. Contact staff for athlete details.'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Alerts View */}
      {viewMode === 'alerts' && !isReadOnly && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Red Alerts */}
          <Card className="bg-zinc-900/50 border-rose-500/30">
            <CardHeader>
              <CardTitle className="text-rose-400 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {language === 'IT' ? 'Allerta Rossa' : 'Red Alerts'} ({redAlerts.length})
              </CardTitle>
              <CardDescription className="text-zinc-500">
                Readiness &lt; 40
              </CardDescription>
            </CardHeader>
            <CardContent>
              {redAlerts.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                  {language === 'IT' ? 'Nessuna allerta rossa' : 'No red alerts'}
                </div>
              ) : (
                <div className="space-y-3">
                  {redAlerts.map((alert, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-sm"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium text-zinc-100">{alert.athlete_name || alert.name}</div>
                        <div className="text-xl font-bold text-rose-400">{alert.readiness_score || alert.readiness}</div>
                      </div>
                      <div className="text-xs text-zinc-500">
                        {alert.team_code} • {alert.athlete_position}
                      </div>
                      {alert.notes && (
                        <ReadableNote testId={`wellness-alert-note-${alert.athlete_id}-${alert.date}`} className="mt-2">{alert.notes}</ReadableNote>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Amber Alerts */}
          <Card className="bg-zinc-900/50 border-amber-500/30">
            <CardHeader>
              <CardTitle className="text-amber-400 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                {language === 'IT' ? 'Allerta Gialla' : 'Yellow Alerts'} ({amberAlerts.length})
              </CardTitle>
              <CardDescription className="text-zinc-500">
                Readiness 40-60
              </CardDescription>
            </CardHeader>
            <CardContent>
              {amberAlerts.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                  {language === 'IT' ? 'Nessuna allerta gialla' : 'No yellow alerts'}
                </div>
              ) : (
                <div className="space-y-3">
                  {amberAlerts.map((alert, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-sm"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium text-zinc-100">{alert.athlete_name || alert.name}</div>
                        <div className="text-xl font-bold text-amber-400">{alert.readiness_score || alert.readiness}</div>
                      </div>
                      <div className="text-xs text-zinc-500">
                        {alert.team_code} • {alert.athlete_position}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Missing View */}
      {viewMode === 'missing' && !isReadOnly && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-zinc-100 flex items-center gap-2">
              <XCircle className="w-5 h-5 text-zinc-500" />
              {language === 'IT' ? 'Risposte Mancanti' : 'Missing Submissions'} ({missing.length})
            </CardTitle>
            <CardDescription className="text-zinc-500">
              {language === 'IT' 
                ? 'Atleti che non hanno ancora inviato il wellness oggi'
                : 'Athletes who have not submitted wellness today'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {missing.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                {language === 'IT' ? 'Tutti hanno risposto!' : 'Everyone has submitted!'}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {missing.map((athlete) => (
                  <div
                    key={athlete.id}
                    className="flex items-center gap-3 p-3 bg-zinc-950/50 border border-zinc-800 rounded-sm"
                  >
                    <div className="w-10 h-10 bg-zinc-800 rounded-sm flex items-center justify-center text-zinc-500">
                      <XCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-zinc-100">{athlete.name}</div>
                      <div className="text-xs text-zinc-500">
                        {athlete.team_code} • {athlete.position}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StaffWellnessPage;
